/* eslint-disable max-lines */
import {
  greenScreenVerificationModalOpenAtom,
  VerifyDesignPopover
} from "@design-lab/features/green-screen-verification";
import { BACKGROUND_TYPE, type BackgroundType } from "@design-lab/shared/constants";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { trackImagePreviewSwitcherClicked } from "@design-lab/shared/lib/analytics";
import { Button, Popover } from "@design-lab/shared/ui";
import { ToggleGroup, ToggleGroupItem } from "@design-lab/shared/ui/toggleGroup";
import { useAtom, useAtomValue } from "jotai";
import { Grid, Shirt } from "lucide-react";
import { useCallback, useRef } from "react";
import { DialogTrigger } from "react-aria-components";
import { LAB_TYPE, type LabType } from "@design-lab/shared/types/labType";
import { backgroundTogglePopoverOpenAtom } from "@design-lab/entities/artwork";

const GreenSquare = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect
      x="2"
      y="2"
      width="12"
      height="12"
      rx="1"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1"
    />
    <rect x="3" y="3" width="10" height="10" rx="0.5" className="fill-green-200" />
  </svg>
);

const GraySquare = ({ className }: { className?: string }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <rect
      x="2"
      y="2"
      width="12"
      height="12"
      rx="1"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1"
    />
    <rect x="3" y="3" width="10" height="10" rx="0.5" className="fill-black" />
  </svg>
);

type BackgroundToggleProps = {
  value: BackgroundType;
  onValueChange: (value: BackgroundType) => void;
  forceFullWidth?: boolean;
  label?: string;
  labType?: LabType;
};

type BackgroundOption = {
  value: BackgroundType;
  icon: typeof Shirt | typeof GreenSquare | typeof Grid;
  label: string;
  testId: string;
};

const ANALYTICS_MODE_MAP: Record<
  BackgroundType,
  "t-shirt" | "green screen" | "chessboard" | "gray"
> = {
  [BACKGROUND_TYPE.SHIRT]: "t-shirt",
  [BACKGROUND_TYPE.GREEN]: "green screen",
  [BACKGROUND_TYPE.CHECKERED]: "chessboard",
  [BACKGROUND_TYPE.GRAY]: "gray"
};

export function BackgroundToggle({
  value,
  onValueChange,
  forceFullWidth = false,
  label,
  labType = LAB_TYPE.DESIGN
}: BackgroundToggleProps) {
  const { isDesktop } = useResponsive();
  const mobileToggleRef = useRef<HTMLDivElement | null>(null);
  const desktopToggleRef = useRef<HTMLDivElement | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useAtom(backgroundTogglePopoverOpenAtom);
  const isVerifyDesignOpen = useAtomValue(greenScreenVerificationModalOpenAtom);

  // Transfers lab gets simplified options with white as default
  const backgroundOptions: BackgroundOption[] =
    labType === LAB_TYPE.TRANSFERS
      ? [
          {
            value: BACKGROUND_TYPE.GRAY,
            icon: GraySquare,
            label: "Gray background",
            testId: "background-toggle-gray"
          },
          {
            value: BACKGROUND_TYPE.CHECKERED,
            icon: Grid,
            label: "Checkered background",
            testId: "background-toggle-checkered"
          }
        ]
      : [
          {
            value: BACKGROUND_TYPE.SHIRT,
            icon: Shirt,
            label: "Show shirt",
            testId: "background-toggle-shirt"
          },
          {
            value: BACKGROUND_TYPE.GREEN,
            icon: GreenSquare,
            label: "Green screen background",
            testId: "background-toggle-green"
          },
          {
            value: BACKGROUND_TYPE.CHECKERED,
            icon: Grid,
            label: "Checkered background",
            testId: "background-toggle-checkered"
          }
        ];

  // For transfers lab, default to white if value is invalid
  const effectiveValue =
    labType === LAB_TYPE.TRANSFERS && !backgroundOptions.some(option => option.value === value)
      ? BACKGROUND_TYPE.GRAY
      : value;

  const currentOption = backgroundOptions.find(option => option.value === effectiveValue);

  const CurrentIcon = currentOption?.icon || Shirt;
  const isShirtSelected = CurrentIcon === Shirt;

  const handleValueChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue) {
        const backgroundType = newValue as BackgroundType;
        trackImagePreviewSwitcherClicked(ANALYTICS_MODE_MAP[backgroundType] || "t-shirt");
        onValueChange(backgroundType);
      }
    },
    [onValueChange]
  );

  const handleMobileValueChange = useCallback(
    (newValue: string | undefined) => {
      if (newValue) {
        const backgroundType = newValue as BackgroundType;
        trackImagePreviewSwitcherClicked(ANALYTICS_MODE_MAP[backgroundType] || "t-shirt");
        onValueChange(backgroundType);
      }
    },
    [onValueChange]
  );

  const renderToggleGroupItems = () =>
    backgroundOptions.map(option => {
      const IconComponent = option.icon;
      return (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          aria-label={option.label}
          data-testid={option.testId}
        >
          <IconComponent className="size-4" />
        </ToggleGroupItem>
      );
    });

  if (forceFullWidth) {
    return (
      <div className="bg-background flex items-center gap-1 rounded-md border p-1">
        {label && <span className="pl-2 text-xs font-semibold">{label}</span>}
        <ToggleGroup
          type="single"
          value={effectiveValue}
          onValueChange={handleValueChange}
          size="sm"
        >
          {renderToggleGroupItems()}
        </ToggleGroup>
      </div>
    );
  }

  return (
    <>
      {/* Mobile popover button */}
      <div className="relative lg:hidden" ref={mobileToggleRef}>
        <DialogTrigger isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <Button
            aria-label="Background options"
            variant="outline"
            size="icon"
            data-testid="background-toggle-open"
            className={`h-14 w-12 !gap-0 ${labType === LAB_TYPE.TRANSFERS ? "bg-gray-200" : "bg-green-200"} flex-col items-center justify-center`}
          >
            <div className="flex size-8 items-center justify-center">
              <CurrentIcon className="fill-background size-6 stroke-[1.3333px]" />
            </div>
            {isShirtSelected && <div className="text-xs font-medium leading-none">Verify</div>}
          </Button>
          <Popover
            className="flex w-12 justify-center py-2"
            placement="top"
            data-testid="background-toggle-popover"
            isNonModal={isVerifyDesignOpen}
          >
            <ToggleGroup
              type="single"
              value={effectiveValue}
              onValueChange={handleMobileValueChange}
              size="sm"
              className={`flex-col`}
            >
              {renderToggleGroupItems()}
            </ToggleGroup>
          </Popover>
        </DialogTrigger>
      </div>

      {/* Desktop inline toggle */}
      <div
        ref={desktopToggleRef}
        className="bg-background hidden w-16 gap-1 rounded-md border p-1 lg:flex lg:h-fit lg:w-fit lg:flex-row"
      >
        {label && <span className="text-sm font-semibold">{label}</span>}
        <ToggleGroup
          type="single"
          value={effectiveValue}
          onValueChange={handleValueChange}
          size="sm"
        >
          {renderToggleGroupItems()}
        </ToggleGroup>
      </div>

      {/* Verification popover - bound to the appropriate toggle based on viewport */}
      {(isDesktop ? desktopToggleRef.current : mobileToggleRef.current) && (
        <VerifyDesignPopover
          anchorElement={(isDesktop ? desktopToggleRef.current : mobileToggleRef.current)!}
          skipButtonText={isDesktop ? "Skip & Add to Cart" : "Skip"}
        />
      )}
    </>
  );
}
